import React from 'react';
import Select from 'react-select';
import BaselineToggle from './Watchlist/BaselineToggle';

function WatchlistFilterControls({
  data,
  selectedTickers,
  setSelectedTickers,
  selectedExpirations,
  setSelectedExpirations,
  selectedScenarios,
  setSelectedScenarios,
  baselineMode,
  setBaselineMode,
  // The Portfolio renders these same filters directly under the blue
  // SimulationSettingsPanel, where the legacy Spotify green clashes. Defaults
  // are the existing green, so the Watchlist is unchanged.
  accentColor = '#1DB954',
  accentTextColor = '#000000',
  surfaceColor = '#121212',
}) {
  // Extract unique tickers from data
  const tickerOptions = Array.from(
    new Set(data.map(item => item.Ticker || item.ticker))
  ).filter(Boolean).map(ticker => ({ value: ticker, label: ticker }));

  // Extract unique expiration dates
  const expirationOptions = Array.from(
    new Set(data.map(item => item.Expiration || item.expiration))
  ).filter(Boolean).map(date => ({ value: date, label: date }));

  // Extract unique scenario percentages
  const scenarioOptions = Array.from(
    new Set(data.map(item => item["Underlying Scenario % Change"] || item.underlying_scenario))
  ).filter(val => val !== null && val !== undefined).map(scenario => ({
    value: scenario,
    label: `${scenario > 0 ? '+' : ''}${scenario}%`
  }));

  const handleTickerChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setSelectedTickers(values);
  };

  const handleExpirationChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setSelectedExpirations(values);
  };

  const handleScenarioChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setSelectedScenarios(values);
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: surfaceColor,
      color: '#FFFFFF',
      borderRadius: '6px',
      borderColor: state.isFocused ? accentColor : '#333',
      boxShadow: state.isFocused ? `0 0 0 1px ${accentColor}` : 'none',
      minHeight: '36px',
      height: '36px',
      "&:hover": { borderColor: accentColor },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: surfaceColor,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? accentColor : surfaceColor,
      color: state.isFocused ? accentTextColor : '#FFFFFF',
      cursor: 'pointer',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: accentColor,
      borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: accentTextColor,
      fontWeight: 600,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: accentTextColor,
      ':hover': {
        backgroundColor: '#ff4d4d',
        color: '#fff',
      },
    }),
    input: (base) => ({ ...base, color: '#FFFFFF' }),
    placeholder: (base) => ({ ...base, color: '#AAAAAA' }),
    singleValue: (base) => ({ ...base, color: '#FFFFFF' }),
  };

  return (
    <div className="filters flex-responsive container-wide mb-6">
      <div className="filter-select">
        <label className="filter-heading">Filter by Ticker</label>
        <Select
          isMulti
          options={tickerOptions}
          value={tickerOptions.filter(opt => selectedTickers.includes(opt.value))}
          onChange={handleTickerChange}
          placeholder="Select tickers..."
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={customStyles}
        />
      </div>

      <div className="filter-select">
        <label className="filter-heading">Filter by Expiration</label>
        <Select
          isMulti
          options={expirationOptions}
          value={expirationOptions.filter(opt => selectedExpirations.includes(opt.value))}
          onChange={handleExpirationChange}
          placeholder="Select expiration dates..."
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={customStyles}
        />
      </div>

      <div className="filter-select">
        <label className="filter-heading">Filter by Scenario %</label>
        <Select
          isMulti
          options={scenarioOptions}
          value={scenarioOptions.filter(opt => selectedScenarios.includes(opt.value))}
          onChange={handleScenarioChange}
          placeholder="Select scenarios..."
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={customStyles}
        />
      </div>

      {setBaselineMode && (
        <div className="filter-select">
          <label className="filter-heading">Compare to</label>
          <div style={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}>
            <BaselineToggle mode={baselineMode} onChange={setBaselineMode} size="md" />
          </div>
        </div>
      )}
    </div>
  );
}

export default WatchlistFilterControls;
